import { useEffect, useState } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Breadcrumb from "../../Components/Breadcrumb";
import { PulseLoader } from "react-spinners";
import {
  moveAuditClosure,
  closeAuditing,
  getAuditReivewDetails,
  getQuestionResponse,
  reviewAuditing,
  generateManualReview,
} from "../../api/common";
import Alert from "../../Components/AlertPopup";
import ConfirmBox from "../../Components/ConfirmBox";
import LoadingOverlay from "../../Components/LoadingOverlay";
import { ERROR_MESSAGE } from "../../constants";
import {
  getTeamMembersSelectOptions,
  getTeamsSelectOptions,
  loadOrganizationLogos,
} from "../../utils";
import PageContainer from "../PageContainer";
import styles from "./style.module.scss";
import {
  SequenceStepper,
  QuestionAccordion,
  QuestionPreviewModal,
  AuditInfoAccordion,
} from "./components";
import { ReviewResponsesModal } from "./components/ReviewResponseModel";

// Table columns configuration
const RESPONSE_TABLE_COLUMNS = [
  { label: "S/No", key: "serialNo" },
  { label: "Question", key: "question" },
  { label: "Auditee Response", key: "response" },
  { label: "Responded On", key: "respondedOn" },
  { label: "Documents", key: "documents" },
  { label: "Evaluation", key: "complianceStatus" },
  { label: "Reason", key: "reason" },
  { label: "Evaluated By", key: "evaluatedBy" },
  { label: "Evaluated On", key: "evaluatedOn" },
];

const ViewAuditing = () => {
  const [showModal, setShowModal] = useState(false);
  const [modelresponse, setmodelresponse] = useState<any>([]);
  const [membersList, setMembersList] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [showToastMessage, setShowToastMessage] = useState("");
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState(
    ERROR_MESSAGE.API_ERROR_MESSAGE,
  );
  const [teamList, setTeamList] = useState<any>([]);
  const [openResponders, setOpenResponders] = useState<{
    [key: string]: boolean;
  }>({});

  const [questionRowData, setQuestionRowData] = useState<any>();
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [showReview, setShowReview] = useState<boolean>(false);
  const [sendQuestionLoading, setSendQuestionLoading] =
    useState<boolean>(false);
  const [auditDetails, setAuditDetails] = useState<any>();
  const [responseLoading, setResponseLoading] = useState(false);
  const [activeSequence, setActiveSequence] = useState<number>(0);

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const fromPath = location.state?.from || "/closure";
  const fromLabel = location.state?.fromLabel || "Audit Closure";

  useEffect(() => {
    const fetchData = async () => {
      const teamMembersOptions = await getTeamMembersSelectOptions();
      const teamOptions = await getTeamsSelectOptions();
      setMembersList(teamMembersOptions);
      setTeamList(teamOptions);
    };
    fetchData();
  }, []);

  const toggleRow = (index: number) => {
    setExpandedRow((prev) => {
      if (prev === index) {
        setOpenResponders({});
        return null;
      }
      return index;
    });
  };

  const handleResponderToggle = (key: string) => {
    setOpenResponders((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  useEffect(() => {
    setLoading(true);
    getAuditReivewDetails(parseInt(id!))
      .then((response: any) => {
        setLoading(false);
        setAuditDetails(response);
      })
      .catch(() => {
        setLoading(false);
        setErrorMessage(ERROR_MESSAGE.API_ERROR_MESSAGE);
        setShowErrorToast(true);
      });
  }, [id]);

  const getConfirmationForClosure = () => {
    setShowConfirm(true);
  };

  const closeAudit = async () => {
    setLoading(true);
    setShowConfirm(false);
    try {
      let message = "";
      if (auditDetails?.stage === 2) {
        await moveAuditClosure(id!);
        message = "Audit successfully moved to closure!";
      } else {
        await closeAuditing(id!);
        message = "Audit successfully closed!";
      }
      setShowToast(true);
      setShowToastMessage(message);
      setLoading(false);
    } catch {
      console.warn("Error occurred in closing/moving the audit");
    }
  };

  const reviewAudit = () => {
    setLoading(true);
    reviewAuditing(id!).then(() => {
      setLoading(false);
      setShowReview(false);
      setShowToast(true);
      setShowToastMessage("Audit successfully passed for Review!");
    });
  };

  // Handle proceeding to next step - marks current sequence as completed
  const handleProceedToNextStep = () => {
    if (questionRowData?.sequences) {
      const updatedSequences = questionRowData.sequences.map(
        (seq: any, index: number) => {
          if (index === activeSequence) {
            return { ...seq, isSequenceCompleted: true };
          }
          return seq;
        },
      );
      setQuestionRowData({ ...questionRowData, sequences: updatedSequences });
      setActiveSequence(activeSequence + 1);
    }
  };

  // Check if a sequence has started (has any responses)
  const isSequenceStarted = (sequence: any) => {
    if (sequence?.currentSequence) return false;
    else {
      return true;
    }
  };

  const [orgTabs, setOrgTabs] = useState<
    { label: string; value: number; logo: string }[]
  >([]);
  const [activeOrg, setActiveOrg] = useState<string>("");
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  // Question Preview Modal state
  const [showQuestionPreview, setShowQuestionPreview] = useState(false);

  const getCurrentSequenceIndex = (sequences: any[]): number => {
    const index = sequences?.findIndex((seq) => seq.currentSequence === true);

    return index !== -1 ? index : 1;
  };

  useEffect(() => {
    const processOrganizationsWithLogos = async () => {
      if (auditDetails?.organizations?.length) {
        const logoMap = await loadOrganizationLogos(auditDetails.organizations);
        const formattedOrgs = auditDetails.organizations.map((org: any) => ({
          label: org.organizationName,
          value: org.organizationId,
          logo: logoMap.get(org.organizationId) || org.organizationLogo,
        }));

        setOrgTabs(formattedOrgs);
        setActiveOrg(formattedOrgs[0].label);
        setResponseLoading(true);

        // TODO: Remove mock data usage - revert to original API call
        getQuestionResponse(parseInt(id!), formattedOrgs[0].value).then(
          (response) => {
            if (response) {
              setQuestionRowData(response);
              setResponseLoading(false);
              setActiveSequence(getCurrentSequenceIndex(response.sequences));
            }
          },
        );
      }
    };
    processOrganizationsWithLogos();
  }, [auditDetails, id]);

  const handleTabChange = (data: any) => {
    setActiveOrg(data.label);
    setIsAccordionOpen(true);
    setResponseLoading(true);

    // TODO: Remove mock data usage - revert to original API call
    getQuestionResponse(parseInt(id!), data.value).then((response) => {
      if (response) {
        setQuestionRowData(response);
        setResponseLoading(false);
      }
    });
  };

  const getAuditorReviewText = (
    questionValidationRequired: boolean,
    responseValidationRequired: boolean,
  ): string => {
    if (!questionValidationRequired && !responseValidationRequired) {
      return "Fully automated (No manual review)";
    }

    const parts: string[] = [];
    if (questionValidationRequired) {
      parts.push("Before sending questions");
    }
    if (responseValidationRequired) {
      parts.push("After AI review responses");
    }

    return parts.join(", ");
  };

  const breadcrumbItems = [
    { label: fromLabel, path: fromPath },
    { label: `Audit ID : ${auditDetails?.auditCode || ""}` },
  ];

  console.log(questionRowData);

  const handleReviewResponses = async () => {
    if (!id) return;

    const logoMap = await loadOrganizationLogos(auditDetails.organizations);
    const formattedOrgs = auditDetails.organizations.map((org: any) => ({
      label: org.organizationName,
      value: org.organizationId,
      logo: logoMap.get(org.organizationId) || org.organizationLogo,
    }));

    const sequenceOrder =
      questionRowData.sequences?.[activeSequence]?.sequenceOrder;

    try {
      setLoading(true);

      await generateManualReview(
        parseInt(id!),
        formattedOrgs[0].value,
        sequenceOrder,
      ).then((data) => {
        setmodelresponse(data);
      });
      setShowModal(true);
    } catch (error) {
      setErrorMessage("Failed to generate manual review");
      setShowErrorToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionsSent = async () => {
    setSendQuestionLoading(true);
    setResponseLoading(true);

    const logoMap = await loadOrganizationLogos(auditDetails.organizations);
    const formattedOrgs = auditDetails.organizations.map((org: any) => ({
      label: org.organizationName,
      value: org.organizationId,
      logo: logoMap.get(org.organizationId) || org.organizationLogo,
    }));

    getQuestionResponse(parseInt(id!), formattedOrgs[0].value).then(
      (response) => {
        if (response) {
          setQuestionRowData(response);
          setResponseLoading(false);
        }
      },
    );
  };

  const handleQuestionPreviewError = (message: string) => {
    setErrorMessage(message);
    setShowErrorToast(true);
  };

  console.log(
    questionRowData?.sequences?.[activeSequence]?.responseValidationRequired &&
      questionRowData?.sequences?.[activeSequence]?.currentSequence &&
      questionRowData?.manualValidationRequired,
  );

  return (
    <PageContainer>
      <LoadingOverlay loading={loading} />

      {auditDetails && (
        <div className={styles.dashboardContainer}>
          {/* Breadcrumb Navigation */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "1rem",
              textAlign: "left",
              width: "100%",
              height: "80px",
            }}
          >
            <Breadcrumb items={breadcrumbItems} />
            <div style={{ textAlign: "right" }}>
              <button
                onClick={getConfirmationForClosure}
                className={styles.close_btn_secondary}
                disabled={responseLoading || !questionRowData?.readyToClosure}
              >
                <FaCheckCircle color="white" />
                {auditDetails?.stage === 2 ? "Move to Closure" : "Close Audit"}
              </button>
            </div>
          </div>

          <AuditInfoAccordion
            auditDetails={auditDetails}
            orgTabs={orgTabs}
            activeOrg={activeOrg}
            onTabChange={handleTabChange}
            isAccordionOpen={isAccordionOpen}
            onToggleAccordion={() => setIsAccordionOpen(!isAccordionOpen)}
          />

          {/* Questionnaire Responses Section */}
          <div className={styles.cardResponse}>
            <div className={styles.titleRow}>
              <div className={styles.title}>Questionnaire Responses</div>
              {questionRowData?.sequences && (
                <div className={styles.stageInfo}>
                  Stage {activeSequence + 1} of{" "}
                  {questionRowData.sequences.length} |{" "}
                  <span className={styles.stageName}>
                    Auditor Review: &nbsp;
                    {getAuditorReviewText(
                      questionRowData.sequences[activeSequence]
                        ?.questionValidationRequired,
                      questionRowData.sequences[activeSequence]
                        ?.responseValidationRequired,
                    )}
                  </span>
                </div>
              )}
            </div>
            <div className={styles.content}>
              {/* Sequence Stepper */}
              {questionRowData?.sequences && (
                <SequenceStepper
                  sequences={questionRowData.sequences}
                  activeSequence={activeSequence}
                  onSequenceChange={setActiveSequence}
                />
              )}

              {/* Questions Section Header with Action Buttons - only show when sequence has started */}
              {questionRowData?.sequences?.[activeSequence]
                ?.currentSequence && (
                <div className={styles.questionsHeaderRow}>
                  <div className={styles.questionsHeader}>Questions</div>
                  <div className={styles.actionButtons}>
                    {questionRowData?.sequences?.[activeSequence]
                      ?.responseValidationRequired &&
                      questionRowData?.sequences?.[activeSequence]
                        ?.currentSequence &&
                      questionRowData?.manualValidationRequired && (
                        <div>
                          <button
                            className={styles.reviewResponsesBtn}
                            onClick={handleReviewResponses}
                          >
                            Review Responses
                          </button>
                        </div>
                      )}
                  </div>
                </div>
              )}

              {responseLoading ? (
                <div className="text-center" style={{ padding: "20px 0" }}>
                  <PulseLoader size={6} color="#4a4a4a" />
                </div>
              ) : !isSequenceStarted(
                  questionRowData?.sequences?.[activeSequence],
                ) &&
                questionRowData?.sequences?.[activeSequence].questions
                  .length === 0 ? (
                // Show Send Questions UI when sequence hasn't started
                <div className={styles.sendQuestionsContainer}>
                  <div className={styles.sendQuestionsIcon}>
                    <svg
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#0a2a63"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 2L11 13" />
                      <path d="M22 2L15 22L11 13L2 9L22 2Z" />
                    </svg>
                  </div>
                  <p className={styles.sendQuestionsText}>
                    Send questions to auditees to begin this stage and collect
                    responses.
                  </p>
                  <button
                    className={styles.sendQuestionsBtn}
                    onClick={() => setShowQuestionPreview(true)}
                  >
                    Send Questions
                  </button>
                </div>
              ) : (
                <div>
                  {questionRowData?.sequences?.[activeSequence]?.questions?.map(
                    (question: any, index: number) => (
                      <QuestionAccordion
                        key={question.questionId}
                        question={question}
                        index={index}
                        isExpanded={expandedRow === question.questionId}
                        onToggle={() => toggleRow(question.questionId)}
                        openResponders={openResponders}
                        onResponderToggle={handleResponderToggle}
                        columns={RESPONSE_TABLE_COLUMNS}
                        responseValidationRequired={
                          questionRowData?.sequences?.[activeSequence]
                            ?.responseValidationRequired
                        }
                        isViewingPastSequence={
                          !questionRowData?.sequences?.[activeSequence]
                            ?.currentSequence
                        }
                      />
                    ),
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmBox
        message={
          auditDetails?.stage === 2
            ? "Are you sure you'd like to proceed moving this audit to closure?"
            : "Are you sure you'd like to close this audit"
        }
        handleClose={() => setShowConfirm(false)}
        show={showConfirm}
        onConfirm={closeAudit}
      />
      <ConfirmBox
        message="Are you sure you want to move the audit to review?"
        handleClose={() => setShowReview(false)}
        show={showReview}
        onConfirm={reviewAudit}
      />
      <Alert
        show={showToast}
        onClose={() => {
          setShowToast(false);
          setShowToastMessage("");
          navigate(auditDetails?.stage === 2 ? "/in-progress" : "/closure");
        }}
        message={showToastMessage}
      />
      <Alert
        show={sendQuestionLoading}
        onClose={() => setSendQuestionLoading(false)}
        message="Questions sent successfully!!"
      />

      {showModal && (
        <ReviewResponsesModal
          onClose={() => setShowModal(false)}
          apiData={modelresponse}
          auditDetails={auditDetails}
        />
      )}

      {/* Question Preview Modal */}
      <QuestionPreviewModal
        show={showQuestionPreview}
        onHide={() => setShowQuestionPreview(false)}
        auditId={parseInt(id!)}
        activeSequence={activeSequence}
        questionRowData={questionRowData}
        auditDetails={auditDetails}
        onQuestionsSent={handleQuestionsSent}
        onError={handleQuestionPreviewError}
      />
    </PageContainer>
  );
};

export default ViewAuditing;
